// src/components/legal/TermsModal.tsx
import React, { useState } from 'react';
import { X, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { TERMS_AND_CONDITIONS, PRIVACY_POLICY, QUICK_SUMMARY } from '../../legal/terms';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'terms' | 'privacy';
}

export const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, initialTab = 'terms' }) => {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>(initialTab);
  const [expandedSections, setExpandedSections] = useState<number[]>([]);

  if (!isOpen) return null;

  const toggleSection = (index: number) => {
    setExpandedSections(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const currentContent = activeTab === 'terms' ? TERMS_AND_CONDITIONS : PRIVACY_POLICY;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--space-4)'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-2xl)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <FileText size={24} color="var(--color-primary)" />
            <h2 style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Legal Documents
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 'var(--radius)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'var(--transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={24} color="var(--color-muted)" />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--color-border)',
          padding: '0 var(--space-6)'
        }}>
          <button
            onClick={() => setActiveTab('terms')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${activeTab === 'terms' ? 'var(--color-primary)' : 'transparent'}`,
              cursor: 'pointer',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              color: activeTab === 'terms' ? 'var(--color-primary)' : 'var(--color-muted)',
              transition: 'var(--transition-fast)'
            }}
          >
            Terms & Conditions
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${activeTab === 'privacy' ? 'var(--color-primary)' : 'transparent'}`,
              cursor: 'pointer',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              color: activeTab === 'privacy' ? 'var(--color-primary)' : 'var(--color-muted)',
              transition: 'var(--transition-fast)'
            }}
          >
            Privacy Policy
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--space-6)'
        }}>
          {/* Quick Summary Banner */}
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-6)'
          }}>
            <h3 style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-bold)',
              color: '#1e40af',
              margin: '0 0 var(--space-2) 0'
            }}>
              Quick Summary
            </h3>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: '#1e3a8a',
              margin: 0,
              lineHeight: '1.6',
              whiteSpace: 'pre-line'
            }}>
              {QUICK_SUMMARY}
            </p>
          </div>

          {/* Last Updated */}
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--color-muted)',
            margin: '0 0 var(--space-4) 0',
            fontStyle: 'italic'
          }}>
            Last updated: {currentContent.lastUpdated}
          </p>

          {/* Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {currentContent.sections.map((section, index) => (
              <div key={index} style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => toggleSection(index)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-4)',
                    backgroundColor: expandedSections.includes(index) ? 'var(--color-surface)' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.includes(index)) {
                      e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.includes(index)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <h4 style={{
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-primary)',
                    margin: 0,
                    textAlign: 'left'
                  }}>
                    {section.title}
                  </h4>
                  {expandedSections.includes(index) ? (
                    <ChevronUp size={20} color="var(--color-primary)" />
                  ) : (
                    <ChevronDown size={20} color="var(--color-muted)" />
                  )}
                </button>
                {expandedSections.includes(index) && (
                  <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-background)'
                  }}>
                    <p style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-secondary)',
                      margin: 0,
                      lineHeight: '1.7',
                      whiteSpace: 'pre-line'
                    }}>
                      {section.content}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--space-6)',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--space-3) var(--space-6)',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-background)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-medium)',
              transition: 'var(--transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary)'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
